import {useEffect, useState} from 'react';
import {DetailGrid, Utility, wdiCore} from '@/components/GridWidget';
import {WdiCardTitle, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, message, Space} from 'antd';
import {initFieldDisplayFormater, TableColumnRanderFormats} from '@/components/WdiBus/TableColumnRanderFormats';
import {NsGrid} from '@/components/GridWidget/GridDefProps';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {ImportModalApp} from '@/components/WdiControls/WdiImport';
import {ResponseStatus} from '@/utils/request';
import FormGoodsEditApp, {FormGoodsViewApp} from './FormGoodsEdit';
import {Enum} from '@/enums/system';
import {GridApi} from '@/services/gridapi';
import {toExcel, ToExcelProps} from '@/components/WdiControls/WdiExcel';
import {Product} from '@/services/mdm/product';
import {ShipTo} from '@/services/mdm/shipto';
import {Position} from '@/services/basic/position';
import {FormGoods, FormSp} from '@/services/sales/sp';
import { FormApi } from '@/services/sales';

const ModalAppTypeEnums = {
  None: 0,
  Insert: 1,
  Edit: 2,
  Import: 3,
  Select: 4,
};

export type DetailForApplyProps = {
  pid?: number,
  data?: any,
  dataSource: any[],
  onChange?: (dataSource: any) => void;
}

const FormGoodsForApplyPanel: React.FC<DetailForApplyProps> = (props) => {
  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [editModalProps, setEditModalProps] = useState<WdiModalProps>({visible: false});
  const [importExcels, setImportExcels] = useState<ToExcelProps[]>([]);
  const {data, dataSource, onChange} = props;

  const onClose = () => {
    setEditModalProps({visible: false, title: "", type: ModalAppTypeEnums.None, id: null});
  };

  const onEditSave = (result?: WdiModalAppResult) => {
    if (result && result.code === ResponseStatus.SUCCESS) {
      if (editModalProps.type === ModalAppTypeEnums.Edit) {
        const row = result?.data;
        const newItem = {...row};
        newItem.uid = editModalProps.data.uid;
        onChange?.(DataStore.edit(dataSource, newItem));
      } else if (editModalProps.type === ModalAppTypeEnums.Insert) {
        const row = result?.data;
        const newItem = {...row};
        onChange?.(DataStore.add(dataSource, newItem));
      }
    }
    onClose();
  };
  const onImportSave = (uploadResult: any) => {
    if (uploadResult.code == ResponseStatus.SUCCESS) {
      const result = uploadResult?.data?.data;
      onChange?.(DataStore.init(result));
    }
    onClose();
  }

  const initImportExcel = () => {
    const productColumns = [
      {name: "productCode5", displayName: "产品编号"},
      {name: "productName5", displayName: "产品名称"},
      {name: "productCode4", displayName: "V码规格编号"},
      {name: "productName4", displayName: "V码规格名称"},
      {name: "productCode3", displayName: "销售小类编号"},
      {name: "productName3", displayName: "销售小类名称"},
      {name: "productCode2", displayName: "销售大类编号"},
      {name: "productName2", displayName: "销售大类名称"},
      {name: "productCode1", displayName: "品牌编号"},
      {name: "productName1", displayName: "品牌名称"},
      {name: "vCode", displayName: "V码"},
      {name: "specCode", displayName: "规格编号"},
      {name: "specName", displayName: "规格名称"},
      {name: "uom", displayName: "主单位名称"},
      {name: "uom1", displayName: "包装1单位名称"},
      {name: "uom1Quantity", displayName: "包装1数量"},
      {name: "uom2", displayName: "包装2单位名称"},
      {name: "uom2Quantity", displayName: "包装2数量"},
      {name: "weightNet", displayName: "净重"},
      {name: "companyCodeOrNames", displayName: "公司"},
    ];
    // 本BU的&激活的&（本人关联品牌下的 or 父级为空的）的产品，
    const productSheet = {
      dataLoading: async () => {
        return await Product.queryForSheet({
          isActive: true,
          productLevel: Enum.ProductLevel.PRODUCT5,
          withPermissions: true,
          withNullOrStaff: true,
        });
      },
      data: null,
      names: productColumns,
      sheetName: "产品",
    };
    // 收货地编号，收货地名称，客户编号，客户名称，四级使用部门（取shipto的boid）
    const shipToColumns = [
      {name: "shipToCode", displayName: "收货地编号"},
      {name: "shipToName", displayName: "收货地名称"},
      {name: "soldToCode", displayName: "客户编号"},
      {name: "soldToName", displayName: "客户名称"},
      {name: "boName", displayName: "四级使用部门"},
    ];
    const shipToSheet = {
      dataLoading: async (params: any) => {
        return await ShipTo.searchExcel({
          withPermissions: true,
          isActive: true
        });
      },
      data: null,
      names: shipToColumns,
      sheetName: "收货地",
    };
    const importExcels: ToExcelProps[] = [];
    importExcels.push({
      name: "全部下载",
      sheetProps: [productSheet, shipToSheet]
    });
    importExcels.push({
      name: "产品列表",
      sheetProps: [productSheet]
    });
    importExcels.push({
      name: "收货地列表",
      sheetProps: [shipToSheet]
    });
    setImportExcels(importExcels);
  }

  useEffect(() => {
    GridApi.searchConfig({id: 'FormGoodsInfo', allowModifyColumns: false}).then((response) => {
      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        showAggr: true,
      });

      initFieldDisplayFormater(settings.formSchema.allFields);

      settings.formSchema.allFields.amount.aggr = TableColumnRanderFormats.sumAggr('amount', 2);

      settings.formSchema.allFields.rowNumber.width = 60;
      settings.formSchema.allFields.shipToCode.width = 200;
      settings.formSchema.allFields.amount.width = 150;
      settings.formSchema.allFields.expectDeliveryDate.width = 150;
      settings.formSchema.allFields.expectArrivalDate.width = 150;

      initImportExcel();
      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    pagination: false
  };
  if (pageGridSettings && pageGridSettings.formSchema && pageGridSettings.formSchema.actions) {
    Utility.setActionCallback(pageGridSettings.formSchema.actions, 'Update', async (grid: any, action: any, record: any) => {
        const context = grid.getContext();
        setEditModalProps({
          visible: true,
          title: '编辑申领明细',
          type: ModalAppTypeEnums.Edit,
          id: null,
          data: {
            pageTypeId: context.params.pageTypeId,
            soldToCode: context.params.soldToCode,
            rowIndex: DataStore.getRowIndex(context.dataSource, record.uid),
            formGoods: record,
            formGoodsList: dataSource,
            uid: record.uid,
          }
        });
      },
    );

    Utility.setActionCallback(pageGridSettings.formSchema.actions, 'SystemDelete', async (grid: any, action: any, record: any) => {
        if (confirm("确认要删除此条货补申领信息吗？")) {
          onChange?.(DataStore.del(dataSource, record.uid));
          message.success("删除成功");
        }
      },
    );
  }

  return (
    <>
      <WdiPanel
        title={<WdiCardTitle iconType={Enum.IconType.DETAIL_INFO} title='本次货补申领信息'/>}
        extra={
          <Space>
            <Button
              type='primary'
              onClick={() => {
                setEditModalProps({
                  visible: true,
                  title: '申领明细导入',
                  type: ModalAppTypeEnums.Import,
                  data: data,
                });
              }}>
              导入
            </Button>
            <Button
              type='primary'
              onClick={() => {
                setEditModalProps({
                  visible: true,
                  title: '新建申领明细',
                  type: ModalAppTypeEnums.Insert,
                  data: Object.assign({}, data, {dataSource: dataSource}),
                });
              }}
              style={{float: 'right'}}>
              新建
            </Button>
          </Space>
        }
      >
        {isLoaded && editModalProps.visible && editModalProps.type === ModalAppTypeEnums.Import && (
          <ImportModalApp
            title={editModalProps.title}
            visible={editModalProps.visible}
            settingsUrl="/expenseapi/spButtons/getGoodsImportSettings"
            uploadingUrl="/expenseapi/spButtons/goodsImportUploading"
            onClose={onClose}
            onSave={onImportSave}
            clientParameters={{
              pageTypeId: data.pageTypeId,
              soldToCode: data.soldToCode,
              formGoods: JSON.stringify(dataSource)
            }}
            importExcels={importExcels}
          />
        )}
        {isLoaded && editModalProps.visible && (editModalProps.type === ModalAppTypeEnums.Edit || editModalProps.type === ModalAppTypeEnums.Insert) && (
          <FormGoodsEditApp
            {...editModalProps}
            onClose={onClose}
            onSave={onEditSave}
          />
        )}
        {isLoaded && (
          <DetailGrid {...pageGridSettings} params={data} autoColumn={['rowNumber']}/>
        )}
      </WdiPanel>
    </>
  );
}
export default FormGoodsForApplyPanel;

export const FormGoodsForApprovalPanel: React.FC<DetailForApplyProps> = (props) => {
  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const {dataSource, onChange} = props;
  const [form, setForm] = useState<API.Form>();
  const [collapsed,setCollapsed] = useState(false);


  const [dispModalProps, setDispModalProps] = useState<WdiModalProps>({visible: false});
  const onDispClose = () => {
    setDispModalProps({visible: false, title: "", id: null, data: null});
  };

  useEffect(() => {
    Promise.all([GridApi.searchConfig({id: 'FormGoodsApproval', allowModifyColumns: true}),
      Position.access({
        useCaseId: Enum.UseCase.SP,
        operateTypeIds: [Enum.OperateType.COMPLETE_DELIVERY]
      }),
      FormSp.get(props.pid ? Number(props.pid) : 0),
      FormApi.get(props.pid ? Number(props.pid) : 0)
    ]).then((dataArray: any[]) => {
      const response = dataArray[0];
      const allowCompleteDelivery = dataArray[1].data;
      const formSp = dataArray[2].data;
      setForm(dataArray[3].data)

      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        showAggr: true,
        showExpandedRow: true,
        filterMenuActions: (actions: any, record: any) => {
          const newActions: any[] = [];
          actions.forEach((item: any) => {
            if (item.name == 'ViewGoodsDetail') {
              newActions.push(item);
            }
            if (item.name == 'CompleteDelivery'
              && formSp && formSp.statusId == Enum.FormStatus.APPROVE_COMPLETED
              && allowCompleteDelivery
              && !record.isDelivered
            ) {
              newActions.push(item);
            }
          });
          return newActions;
        }
      });

      initFieldDisplayFormater(settings.formSchema.allFields);

      settings.formSchema.allFields.amount.aggr = TableColumnRanderFormats.sumAggr('amount', 2);
      settings.formSchema.allFields.amountDelivered.aggr = TableColumnRanderFormats.sumAggr('amountDelivered', 2);

      settings.formSchema.allFields.rowNumber.width = 70;
      settings.formSchema.allFields.shipToCode.width = 200;
      settings.formSchema.allFields.amount.width = 150;
      settings.formSchema.allFields.amountDelivered.width = 150;
      settings.formSchema.allFields.expectDeliveryDate.width = 150;
      settings.formSchema.allFields.expectArrivalDate.width = 150;
      settings.formSchema.allFields.isDelivered.width = 100;

      Utility.setActionCallback(
        settings.formSchema.actions,
        'ViewGoodsDetail',
        async (grid: any, action: any, record: any) => {
          setDispModalProps({
            visible: true,
            title: '申领明细信息',
            data: {
              formGoods: record
            }
          });
        },
      );
      if (allowCompleteDelivery) {
        Utility.setActionCallback(settings.formSchema.actions,
          'CompleteDelivery',
          (grid: any, action: any, record: any) => {
            if (window.confirm("确定要设置发货完成？")) {
              const args: API.ButtonArgs = {
                formId: record.formId,
                key: "CompleteDelivery",
                items: [{name: "formGoodsId", value: String(record.formGoodsId)}]
              }
              FormSp.Buttons.completeDelivery(args).then(resp => {
                if (resp.code == ResponseStatus.SUCCESS) {
                  message.success('操作成功!').then();
                  FormGoods.getForApply(record.formId).then((response) => {
                    onChange?.(response.data);
                  });
                } else {
                  message.error(resp.message).then();
                }
              })
            }
          });
      }

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);
  const maxAllowedFields = ["rowNumber", "shipToCode", "amount", "expectDeliveryDate", "expectArrivalDate",
    "amountDelivered", "isDelivered", "remark", "address", "deliveredSetupDate", "deliveredOperatorId"];
  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    initRowFieldConfigs: (record: any) => {
      return maxAllowedFields;
    },
    pagination: false
  };

  // 字段为 行号，活动明细编号，客户，收货地，要求出库日期，要求到货日期，地址，发货完成，发货完成设置日期，发货完成设置人，备注，产品，领用单位，
  // 正常供货价，促销供货价，申领单价、申领数量，申领金额，发货数量，发货金额
  // 注：客户、收货地、产品分为编号名称两列，产品及后面的字段用FormGoodsDetail的字段显示（按form_goods_id left join到form_goods上）
  const exportButton = () => {
    return <Button
      style={{float: 'right'}}
      type='primary'
      onMouseOver={() => setCollapsed(false)}
      onMouseOut={() => setCollapsed(true)}
      onClick={() => {
        const exportColumns = [
          {name: "rowNumber", displayName: "行号"},
          {name: "detailId", displayName: "申领明细ID"},
          {name: "soldToCode", displayName: "客户编号"},
          {name: "soldToName", displayName: "客户名称"},
          {name: "shipToCode", displayName: "收货地编号"},
          {name: "shipToName", displayName: "收货地名称"},
          {
            name: "expectDeliveryDate",
            displayName: "要求出库日期",
            type: Enum.FieldType.DATE_TIME,
            format: 'yyyy-MM-dd'
          },
          {
            name: "expectArrivalDate",
            displayName: "要求到货日期",
            type: Enum.FieldType.DATE_TIME,
            format: 'yyyy-MM-dd'
          },
          {name: "address", displayName: "地址"},
          {name: "isDelivered", displayName: "发货完成", type: Enum.FieldType.BOOLEAN},
          {name: "deliveredSetupDate", displayName: "发货完成设置日期"},
          {name: "deliveredOperatorName", displayName: "发货完成设置人"},
          {name: "remark", displayName: "备注"},
          {name: "productCode", displayName: "产品编号"},
          {name: "productName", displayName: "产品名称"},
          {name: "uomName", displayName: "领用单位"},
          // 正常供货价，促销供货价，申领单价、申领数量，申领金额，发货数量，发货金额
          {name: "price", displayName: "正常供货价"},
          {name: "priceDiscount", displayName: "促销供货价"},
          {name: "unitPrice", displayName: "申领单价"},
          {name: "quantity", displayName: "申领数量"},
          {name: "amount", displayName: "申领金额"},
          {name: "shippingQuantity", displayName: "发货数量"},
          {name: "shippingAmount", displayName: "发货金额"},
        ];

        const innerSource: any[] = [];
        (dataSource ?? []).forEach((fg) => {
          (fg?.formGoodsDetails ?? []).forEach((fgDetail: API.FormGoodsDetailVo) => {
            innerSource.push({
              formNo: form?.formNo,
              detailId:  [form?.formNo, fg?.formGoodsId, fgDetail?.formGoodsDetailId].join('-'),
              rowNumber: fg.rowNumber,
              soldToCode: fg.soldToCode,
              soldToName: fg.soldToName,
              shipToCode: fg.shipToCode,
              shipToName: fg.shipToName,
              shopCode: fg.shopCode,
              shopName: fg.shopName,
              expectDeliveryDate: fg.expectDeliveryDate,
              expectArrivalDate: fg.expectArrivalDate,
              address: fg.address,
              isDelivered: fg.isDelivered,
              deliveredSetupDate: fg.deliveredSetupDate,
              deliveredOperatorName: fg.deliveredOperator,
              remark: fg.remark,
              productCode: fgDetail.productCode,
              productName: fgDetail.productName,
              uomName: fgDetail.uomName,
              price: fgDetail.price,
              priceDiscount: fgDetail.priceDiscount,
              unitPrice: fgDetail.unitPrice,
              quantity: fgDetail.quantity,
              amount: fgDetail.amount,
              shippingQuantity: fgDetail.shippingQuantity,
              shippingAmount: fgDetail.shippingAmount,
            })
          })
        })

        const spDetailSheet = {
          data: innerSource,
          names: exportColumns,
          sheetName: "货补核销申领明细",
          cells: {
            quantity: (fieldObject: any, row: any, value: any) => {
              return wdiCore.Number.ToNumber(row.quantity, 5)
            },
          }
        };
        toExcel({
          name: form?.formNo + "_" + wdiCore.Date.ToString(new Date()) + ".xlsx",
          sheetProps: [spDetailSheet]
        })
      }}>
      {'导出'}
    </Button>
  }

  return (
    <>
      <WdiPanel
        title={<WdiCardTitle title='本次货补申领明细'/>}
        collapsed={collapsed}
        extra={
          <Space>
            {exportButton()}
          </Space>
        }
      >
        {
          isLoaded &&
          dispModalProps.visible &&
          <FormGoodsViewApp
            {...dispModalProps}
            onClose={onDispClose}
            form={form}
          />
        }
        {isLoaded && <DetailGrid {...pageGridSettings} />}
      </WdiPanel>
    </>
  );
}
